/**
 * 通知相关服务
 * 包含获取通知列表、未读数量、标记已读等功能
 */

import { supabase } from './supabase';

// 通知类型（与数据库枚举对应）
export type NotificationType = 'like' | 'comment' | 'follow' | 'mention' | 'agent_post' | 'agent_comment' | 'system';

// 作者类型
type AuthorType = 'human' | 'agent';

// 通知详情类型（从视图获取）
export interface NotificationDetail {
  id: string;
  user_id: string;
  type: NotificationType;
  title: string | null;
  content: string | null;
  related_id: string | null;
  related_type: string | null;
  actor_id: string | null;
  actor_type: AuthorType | null;
  actor_name: string | null;
  actor_avatar: string | null;
  is_read: boolean;
  read_at: string | null;
  created_at: string;
  related_image?: string | null;
  related_post_id?: string | null;     // 评论所属帖子ID
  parent_comment_id?: string | null;   // 父评论ID（子评论场景）
}

// 通知统计
export interface NotificationStats {
  total: number;
  unread: number;
  byType: Record<NotificationType, number>;
}

/**
 * 获取用户的通知列表（使用视图）
 */
export async function getNotifications(
  userId: string,
  type?: NotificationType,
  limit: number = 50,
  offset: number = 0
): Promise<NotificationDetail[]> {
  let query = supabase
    .from('v_notification_details')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(limit)
    .range(offset, offset + limit - 1);

  // 按类型筛选
  if (type) {
    query = query.eq('type', type);
  }

  const { data, error } = await query;

  if (error) {
    console.error('获取通知列表失败:', error);
    return [];
  }

  // 获取相关帖子图片
  // 1. 帖子类型：related_id 即为帖子ID
  // 2. 评论类型：related_post_id 为帖子ID
  const notifications = data || [];

  // 收集所有需要获取图片的帖子ID
  const postIdsFromPostType = notifications
    .filter(n => n.related_id && n.related_type === 'post')
    .map(n => n.related_id!);

  const postIdsFromCommentType = notifications
    .filter(n => n.related_post_id && n.related_type === 'comment')
    .map(n => n.related_post_id!);

  const allRelatedPostIds = [...new Set([...postIdsFromPostType, ...postIdsFromCommentType])];

  const postImageMap: Record<string, string | null> = {};

  if (allRelatedPostIds.length > 0) {
    const { data: posts } = await supabase
      .from('posts')
      .select('id, images')
      .in('id', allRelatedPostIds);

    posts?.forEach(p => {
      postImageMap[p.id] = p.images && p.images.length > 0 ? p.images[0] : null;
    });
  }

  // 组合完整的通知信息
  return notifications.map(n => {
    // 确定图片来源
    let relatedImage: string | null = null;
    if (n.related_type === 'post' && n.related_id) {
      relatedImage = postImageMap[n.related_id] || null;
    } else if (n.related_type === 'comment' && n.related_post_id) {
      relatedImage = postImageMap[n.related_post_id] || null;
    }

    return {
      ...n,
      related_image: relatedImage,
    };
  });
}

/**
 * 获取未读通知数量
 */
export async function getUnreadCount(userId: string): Promise<number> {
  const { count, error } = await supabase
    .from('notifications')
    .select('*', { count: 'exact', head: true })
    .eq('user_id', userId)
    .eq('is_read', false);

  if (error) {
    console.error('获取未读通知数量失败:', error);
    return 0;
  }

  return count || 0;
}

/**
 * 获取各类型未读数量（直接查询统计）
 */
export async function getUnreadCountByType(userId: string): Promise<Record<NotificationType, number>> {
  // 直接查询每个类型的未读数量
  const { data, error } = await supabase
    .from('notifications')
    .select('type')
    .eq('user_id', userId)
    .eq('is_read', false);

  if (error) {
    console.error('获取各类型未读数量失败:', error);
    // 返回默认值
    return {
      like: 0,
      comment: 0,
      follow: 0,
      mention: 0,
      agent_post: 0,
      agent_comment: 0,
      system: 0,
    };
  }

  // 手动统计各类型数量
  const result: Record<NotificationType, number> = {
    like: 0,
    comment: 0,
    follow: 0,
    mention: 0,
    agent_post: 0,
    agent_comment: 0,
    system: 0,
  };

  data?.forEach((item: { type: NotificationType }) => {
    result[item.type] = (result[item.type] || 0) + 1;
  });

  return result;
}

/**
 * 标记单个通知为已读
 */
export async function markAsRead(notificationId: string): Promise<boolean> {
  const { error } = await supabase
    .from('notifications')
    .update({
      is_read: true,
      read_at: new Date().toISOString(),
    })
    .eq('id', notificationId);

  if (error) {
    console.error('标记通知已读失败:', error);
    return false;
  }

  return true;
}

/**
 * 批量标记通知为已读
 */
export async function markMultipleAsRead(notificationIds: string[]): Promise<boolean> {
  if (notificationIds.length === 0) return true;

  const { error } = await supabase
    .from('notifications')
    .update({
      is_read: true,
      read_at: new Date().toISOString(),
    })
    .in('id', notificationIds);

  if (error) {
    console.error('批量标记通知已读失败:', error);
    return false;
  }

  return true;
}

/**
 * 标记所有通知为已读
 */
export async function markAllAsRead(userId: string): Promise<boolean> {
  const { error } = await supabase
    .from('notifications')
    .update({
      is_read: true,
      read_at: new Date().toISOString(),
    })
    .eq('user_id', userId)
    .eq('is_read', false);

  if (error) {
    console.error('标记所有通知已读失败:', error);
    return false;
  }

  return true;
}

/**
 * 标记某类型所有通知为已读
 */
export async function markAllAsReadByType(userId: string, type: NotificationType): Promise<boolean> {
  const { error } = await supabase
    .from('notifications')
    .update({
      is_read: true,
      read_at: new Date().toISOString(),
    })
    .eq('user_id', userId)
    .eq('type', type)
    .eq('is_read', false);

  if (error) {
    console.error('标记某类型通知已读失败:', error);
    return false;
  }

  return true;
}

/**
 * 获取通知统计信息
 */
export async function getNotificationStats(userId: string): Promise<NotificationStats> {
  // 获取总数
  const { count: total } = await supabase
    .from('notifications')
    .select('*', { count: 'exact', head: true })
    .eq('user_id', userId);

  // 获取未读数
  const { count: unread } = await supabase
    .from('notifications')
    .select('*', { count: 'exact', head: true })
    .eq('user_id', userId)
    .eq('is_read', false);

  // 获取各类型数量
  const byType = await getUnreadCountByType(userId);

  return {
    total: total || 0,
    unread: unread || 0,
    byType,
  };
}

/**
 * 删除单个通知
 */
export async function deleteNotification(notificationId: string): Promise<boolean> {
  const { error } = await supabase
    .from('notifications')
    .delete()
    .eq('id', notificationId);

  if (error) {
    console.error('删除通知失败:', error);
    return false;
  }

  return true;
}

/**
 * 清空某类型所有通知（或全部通知）
 * @param userId 用户ID
 * @param type 通知类型，不传则清空全部
 */
export async function clearNotifications(userId: string, type?: NotificationType): Promise<boolean> {
  let query = supabase
    .from('notifications')
    .delete()
    .eq('user_id', userId);

  if (type) {
    query = query.eq('type', type);
  }

  const { error } = await query;

  if (error) {
    console.error('清空通知失败:', error);
    return false;
  }

  return true;
}